import {
  ID,
  overdueSectionID,
  ReorderItem,
  ReorderResult,
  Task,
  TaskListItemTypes,
  UpdateTask,
  WeekDays,
} from '@griply/types';
import { endOfDay, startOfDay } from 'date-fns';
import cloneDeep from 'lodash/cloneDeep';

import { getTaskPlanning } from '../get-task-planning';
import { createPlannedOccurrenceForDate } from '../schedules';
import { filterReorderableChildIds } from './filter-reorderable-child-ids';
import { getReorderSectionIds } from './get-reorder-section-ids';
import { getTaskIdFromTreeId } from './get-task-id-from-tree-id';
import {
  getUpcomingSectionDate,
  UpcomingSectionPrefixes,
} from './get-upcoming-section-date';

export type OnUpcomingDeadlineReorderOptions = {
  updatedItems: readonly ReorderItem[];
  tasksMap: Record<ID, Task>;
  today: Date;
  weekStartsOn: WeekDays;
  prefixes: UpcomingSectionPrefixes;
  taskIdResolver?: (id: ID) => ID;
};

export const onUpcomingDeadlineReorder = ({
  updatedItems,
  tasksMap,
  today,
  weekStartsOn,
  prefixes,
  taskIdResolver = getTaskIdFromTreeId,
}: OnUpcomingDeadlineReorderOptions): ReorderResult => {
  const updatedTasks: UpdateTask[] = [];
  const updatedSections: ReorderResult['updatedSections'] = [];

  const dateItems = updatedItems.filter(
    (item) => item.type === TaskListItemTypes.Date,
  );

  updatedItems.forEach((item) => {
    if (item.type === TaskListItemTypes.Task) {
      const taskId = taskIdResolver(item.id);
      if (!tasksMap[taskId]) {
        return;
      }

      const task = tasksMap[taskId];
      const taskToUpdate = cloneDeep(task);

      const { newSectionId, oldSectionId } = getReorderSectionIds(
        item.id,
        dateItems,
      );

      if (!newSectionId || !oldSectionId) {
        return;
      }

      if (newSectionId === oldSectionId) {
        return;
      }

      if (newSectionId === overdueSectionID) {
        return;
      }

      const newDate = getUpcomingSectionDate(newSectionId, prefixes);
      if (!newDate) {
        return;
      }

      const newDeadline = endOfDay(newDate);

      if (task.schedules?.length) {
        const oldDate =
          oldSectionId === overdueSectionID
            ? endOfDay(new Date(0))
            : getUpcomingSectionDate(oldSectionId, prefixes);

        if (!oldDate) {
          return;
        }

        const [plannedOccurrence] = getTaskPlanning(task, {
          startDate: startOfDay(oldDate),
          endDate: endOfDay(oldDate),
          today,
          weekStartsOn,
        });

        const schedules = createPlannedOccurrenceForDate(
          task.schedules,
          newDeadline,
          {
            timeslot: plannedOccurrence?.timeslot ?? null,
            originalDate: oldDate,
            today,
            weekStartsOn,
          },
        );

        updatedTasks.push({ id: task.id, schedules });
        return;
      }

      taskToUpdate.endStrategy = {
        deadline: newDeadline,
        completionCount: taskToUpdate.endStrategy?.completionCount ?? null,
      };
      taskToUpdate.schedules = null;
      updatedTasks.push(taskToUpdate);
      return;
    }

    if (item.type === TaskListItemTypes.Date) {
      updatedSections.push({
        id: item.id,
        taskSorting: filterReorderableChildIds(
          item.children?.map(taskIdResolver),
        ),
      });
    }
  });

  return {
    updatedSections,
    updatedTasks,
  };
};
